import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { webStorage, clearApiCache } from 'sidecar-utility-library'

import { isActionRelevant, clearWebStorage } from './utils'
import { clearChatAttributes } from 'utils/chat/useChatWidget'
import { clearMemberAppSession } from 'utils/memberAppSession'
import { clearAuthedUser } from 'store/Auth'
import {
  clearNewMessage,
  clearRequestType,
  clearPopTartHistory,
} from 'store/MemberCommunication'

export const handleAuth = () => next => action => {
  if (!isActionRelevant(action, /(LOGIN|REGISTER)_SUCCESS/)) {
    return next(action)
  }

  const {
    payload: { hasPurchased },
  } = action

  // default values for any user that logs in
  // aud: accountUuid
  // cud: coverageUuid (likely deprecated?)
  // sub: memberUuid
  webStorage.setItem('aud', action.payload.aud)
  webStorage.setItem('token', action.payload.token)
  webStorage.setItem('sub', action.payload.sub)

  // these values aren't always available so check first.
  action.payload.cud && webStorage.setItem('cud', action.payload.cud)
  action.payload.email && webStorage.setItem('email', action.payload.email)

  // List of member UUIDs that the user has HIPAA access to
  webStorage.setItem(
    'accountMemberAccessMap',
    JSON.stringify(action.payload.accountMemberAccessMap),
  )

  // In order for individualized push notifications to work properly, we need
  // to associate the member with their device. When this app is running as
  // a native app (wrapped in Flutter), we can use a custom `BrazeIdentifier`
  // channel to send back the member UUID so that the app can associate the
  // device with the member through its Braze integration. This JSON can also
  // be extended to send other information to the app as needed.
  if (window.BrazeIdentifier) {
    // Send user data back to Flutter
    window.BrazeIdentifier.postMessage(
      JSON.stringify({
        memberUuid: action.payload.sub,
      }),
    )
  }

  // when the user just registers, we'll need to fetch their account metadata
  // and the list of applicants. This flag tells those things to do that when
  // they run or mount or whatever.
  //TODO: there's probably a better way of doing this.
  if (/REGISTER/.test(action.type)) {
    // This flag will be set to false after account metadata is fetched
    // and then handled in handleFetchAccountMetadata middleware
    webStorage.setItem('justRegistered', true)
  }

  // if the user has purchased a policy, we need to set this flag so that
  // we can show the proper navigation items.
  if (hasPurchased) {
    webStorage.setItem('hasPolicy', true)
  }

  // we set/update this for Tag Manager
  window.v_member_id = action.payload.sub

  // identify member for heap analytics
  window.heap.identify(action.payload.sub)

  // Add the memberUuid to the datadog logs
  datadogLogs.setUser({ id: action.payload.sub })
  datadogRum.setUser({ id: action.payload.sub })

  // Members and the authed user are not fetched here. This action sets
  // `Auth.authToken`, which drives App's boot effect, and that effect already
  // owns the whole sequence: it awaits members (using the response to detect
  // an invalid session, WEB-1787) before fetching the authed user, account
  // metadata and the inbox count. It keys off any authToken change, so it
  // also covers the LOGIN_FROM_LOCAL rehydrate that this middleware never
  // saw — dispatching here only duplicated a heavy `/accounts/{aud}/members`.
  return next(action)
}

/** Handles the side effects for when a member is logged out. */
export const handleLogout =
  ({ dispatch }) =>
  next =>
  action => {
    // there is a condition where a member can get logged out but the SUCCESS
    // action isn't called. To be safe we're going to clear things even when
    // a logout is requested, whether it's successful or not.
    const relevantActions = /LOGOUT_(SUCCESS|REQUEST)|FORCE_LOGOUT/
    if (!isActionRelevant(action, relevantActions)) return next(action)

    // makes sure that we make fresh requests (without old user info)
    clearApiCache()

    // first we clear out any attributes we've set but then we also need to
    // reset the skill and product ones so that the widget can still route
    // the chat to the right agent.
    clearChatAttributes()

    // this clears all the values out of webStorage but it might be
    // a bit overkill.
    clearWebStorage()

    // End member-app's session too (its cookie is HttpOnly, so it clears it)
    clearMemberAppSession()

    // Here we should clear poptart data on log out and force log out
    clearNewMessage()
    clearRequestType()
    clearPopTartHistory()

    // remember to unset the member_id when we logout
    window.v_member_id = '(not set)'

    // de-identify member for logs, monitors and analytics
    datadogLogs.clearUser()
    datadogRum.clearUser()
    window.heap.resetIdentity()

    // we clear out the authedUser so that we can use it to trigger the
    // LDContextIdentifier component to update the LD context to anonymous.
    dispatch(clearAuthedUser)

    return next(action)
  }
